import { Fragment, Slice } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';

import { TiptapNodeType } from '../../tiptapTypes';
import { isWholeTaskItemTextSelection, TASK_ITEM_TEXT_DEPTH } from './taskListItemTextRangeUtils';

/** Whether a slice is one closed task list holding exactly one task item. */
const isClosedSingleTaskItemSlice = (slice: Slice): boolean => {
    // Closed at both ends, which is what `transformCopied` produces. Any ordinary copy is open at
    // the depth its selection cut into, so this never matches a selection the user made themselves.
    if (slice.openStart !== 0 || slice.openEnd !== 0) return false;

    // A single top level node, so a copy spanning a list and anything beside it is left alone.
    if (slice.content.childCount !== 1) return false;

    const taskList = slice.content.firstChild;

    return taskList?.type.name === TiptapNodeType.taskList && taskList.childCount === 1;
};

/**
 * Keeps a whole-task-item copy usable outside the editor without changing how it pastes inside one.
 *
 * Copying all of a task item's text is a text selection, which ProseMirror serialises as a bare
 * paragraph, recording the task item only in the private `data-pm-slice` attribute. That loses the
 * item's chip data, and leaves nothing a board paste can recognise as a task list.
 * `transformCopied` therefore puts the item back inside its task list, closed, so the clipboard
 * carries the same `ul`/`li` markup a multi-item copy does.
 *
 * Only the item's own text comes along. Selecting one item's text says nothing about its sub-tasks,
 * and the user can always extend the selection over them, so taking them too would copy more than
 * was asked for.
 *
 * A closed slice would paste as a nested list when dropped into a task item's text, where a
 * multi-item copy merges its text inline. `transformPasted` re-opens the slice to the depth a
 * multi-item copy has, so both paste identically. The clipboard HTML is unaffected, so a board
 * paste and other applications still see the whole item.
 *
 * A cut runs this same copy path first; the chips it leaves behind are cleared separately, by
 * `createTaskListItemCutChipRemovalPlugin`.
 */
export const createTaskListItemCopyExpansionPlugin = (): Plugin =>
    new Plugin({
        key: new PluginKey('taskListItemCopyExpansion'),
        props: {
            transformCopied: (slice, view) => {
                const { $from, $to } = view.state.selection;

                if (!isWholeTaskItemTextSelection($from, $to)) return slice;

                const taskItem = $from.node($from.depth - 1);
                const taskList = $from.node($from.depth - 2);

                // Keep the attrs, drop everything after the item's own text - its sub-task list.
                const itemWithoutSubTasks = taskItem.copy(Fragment.from($from.parent));

                return new Slice(Fragment.from(taskList.copy(Fragment.from(itemWithoutSubTasks))), 0, 0);
            },
            transformPasted: (slice) =>
                isClosedSingleTaskItemSlice(slice)
                    ? new Slice(slice.content, TASK_ITEM_TEXT_DEPTH, TASK_ITEM_TEXT_DEPTH)
                    : slice,
        },
    });
